/** WhatsApp / seed JSON may use `title`; editor canonical field is `label`. */

export function getButtonLabel(btn) {
  if (!btn) return '';
  const text = btn.label ?? btn.title ?? btn.id;
  return text != null && String(text).trim() !== '' ? String(text) : 'Button';
}

export function normalizeButton(btn) {
  if (!btn) return btn;
  const label = getButtonLabel(btn);
  const { title, ...rest } = btn;
  return { ...rest, label };
}

export function normalizeFlowDefinition(def) {
  if (!def?.nodes?.length) return def;

  // Normalize button labels (title → label) and sync message.list_dynamic configuration
  const nodes = def.nodes.map((node) => {
    if (node.type === 'message.list_dynamic') {
      const outgoingEdges = (def.edges || []).filter(
        (e) => e.from === node.id && e.event !== 'button_reply'
      );
      const targetId = outgoingEdges[0]?.to || '';
      return {
        ...node,
        config: {
          ...node.config,
          on_select_node_id: targetId || node.config?.on_select_node_id || '',
        },
      };
    }
    if (
      node.type !== 'message.interactive_buttons' &&
      node.type !== 'message.template'
    ) {
      return node;
    }
    const buttons = (node.config?.buttons || []).map(normalizeButton);
    return { ...node, config: { ...node.config, buttons } };
  });

  // --- Cleanup 1: spurious sibling chains between button-reply targets ---
  // Both source AND target are button-reply targets of the same template node.
  const buttonReplyEdges = (def.edges || []).filter((e) => e.event === 'button_reply');
  const buttonTargetIds = new Set(buttonReplyEdges.map((e) => e.to).filter(Boolean));

  // --- Cleanup 2: spurious completed edges from no-branch nodes to yes-branch nodes ---
  // When nodes are added sequentially, addNode creates a completed chain between every
  // consecutive node. Later, yes/no routing is wired properly, but the old 'completed'
  // links between failure-path nodes and success-path nodes of the same parent remain.
  //
  // Rule: a 'completed' edge from node A → node B is spurious if:
  //   - there exists a parent P that has BOTH a 'no' edge to A AND a 'yes' edge to B
  //     (meaning A is in the failure branch and B is in the success branch of the same gate)
  const yesTargets = new Map(); // parentId → Set of yes-target ids
  const noTargets = new Map(); // parentId → Set of no-target ids
  ; (def.edges || []).forEach((e) => {
    if (e.event === 'yes' && e.from && e.to) {
      if (!yesTargets.has(e.from)) yesTargets.set(e.from, new Set());
      yesTargets.get(e.from).add(e.to);
    }
    if (e.event === 'no' && e.from && e.to) {
      if (!noTargets.has(e.from)) noTargets.set(e.from, new Set());
      noTargets.get(e.from).add(e.to);
    }
  });

  // Build a set of (fromId, toId) pairs that are spurious cross-branch completed links
  const spuriousPairs = new Set();
  yesTargets.forEach((yesSet, parentId) => {
    const noSet = noTargets.get(parentId);
    if (!noSet) return;
    noSet.forEach((noNode) => {
      yesSet.forEach((yesNode) => {
        spuriousPairs.add(`${noNode}→${yesNode}`);
      });
    });
  });

  // --- Cleanup 3: spurious completed edges between sibling branch targets of the same parent ---
  const parentToTargets = new Map();
  (def.edges || []).forEach((e) => {
    if (e.from && e.to) {
      if (!parentToTargets.has(e.from)) {
        parentToTargets.set(e.from, new Set());
      }
      parentToTargets.get(e.from).add(e.to);
    }
  });

  const siblingPairs = new Set();
  parentToTargets.forEach((targetsSet) => {
    if (targetsSet.size > 1) {
      const targetsArray = Array.from(targetsSet);
      for (let i = 0; i < targetsArray.length; i++) {
        for (let j = 0; j < targetsArray.length; j++) {
          if (i !== j) {
            siblingPairs.add(`${targetsArray[i]}→${targetsArray[j]}`);
          }
        }
      }
    }
  });

  // Build a set of (fromId → toId) pairs that have a button_reply edge,
  // so we can strip any completed edge that duplicates the same source→target.
  const buttonReplyPairs = new Set(
    buttonReplyEdges
      .filter((e) => e.from && e.to)
      .map((e) => `${e.from}→${e.to}`)
  );

  const edges = (def.edges || []).filter((e) => {
    if (e.event === 'completed') {
      // Strip button-reply sibling chain
      if (buttonTargetIds.has(e.from) && buttonTargetIds.has(e.to)) return false;
      // Strip cross-branch (no-side → yes-side) sequential artifacts
      if (e.from && e.to && spuriousPairs.has(`${e.from}→${e.to}`)) return false;
      // Strip completed edge when a button_reply edge already covers the same source→target
      if (e.from && e.to && buttonReplyPairs.has(`${e.from}→${e.to}`)) return false;
      // Strip completed edge between sibling branch targets of the same parent
      // ONLY when those siblings come from button_reply edges (not regular linear chains)
      const fromIsButtonTarget = buttonTargetIds.has(e.from);
      const toIsButtonTarget = buttonTargetIds.has(e.to);
      if (fromIsButtonTarget && toIsButtonTarget && siblingPairs.has(`${e.from}→${e.to}`)) return false;
    }
    return true;
  });

  return { ...def, nodes, edges };
}

